// src/components/ClientChildrenListModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Table, InputGroup, Form, Pagination } from 'react-bootstrap';
import clientMinorService from '../services/clientMinorService';

// Petit helper de normalisation (accents/casse)
const normalize = (s) =>
  (s ?? '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

export default function ClientChildrenListModal({ show, onHide, parent, initialItems = [] }) {
  const [items, setItems] = useState(initialItems);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  // Pagination locale
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (!show || !parent?.id) return;
    // On recharge pour être sûr d'avoir la dernière liste (ex: si modifiée ailleurs)
    setLoading(true);
    clientMinorService
      .listByParent(parent.id)
      .then(setItems)
      .finally(() => setLoading(false));
  }, [show, parent?.id]);

  // Reset pagination quand recherche ou items changent
  useEffect(() => { setPage(1); }, [q, items]);

  const filtered = useMemo(() => {
    const needle = normalize(q);
    if (!needle) return items;
    return items.filter(it =>
      normalize(`${it.firstName} ${it.lastName} ${it.relation} ${it.email} ${it.phone}`).includes(needle)
    );
  }, [items, q]);

  // Pager
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * pageSize;
  const visible = useMemo(() => filtered.slice(start, start + pageSize), [filtered, start, pageSize]);

  const renderPagination = () => (
    <div className="d-flex justify-content-between align-items-center mt-2">
      <div className="text-muted">Résultats : {total} • Page {current}/{totalPages}</div>
      <div className="d-flex align-items-center">
        <Form.Select
          size="sm"
          value={pageSize}
          onChange={e => setPageSize(Number(e.target.value))}
          className="me-2"
          style={{ width: 90 }}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </Form.Select>
        <Pagination className="mb-0">
          <Pagination.Prev disabled={current === 1} onClick={() => setPage(p => Math.max(1, p - 1))} />
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - current) <= 1)
            .map((p, idx, arr) => {
              const prev = arr[idx - 1];
              const needEllipsis = prev && p - prev > 1;
              return (
                <React.Fragment key={p}>
                  {needEllipsis && <Pagination.Ellipsis disabled />}
                  <Pagination.Item active={p === current} onClick={() => setPage(p)}>
                    {p}
                  </Pagination.Item>
                </React.Fragment>
              );
            })}
          <Pagination.Next disabled={current === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} />
        </Pagination>
      </div>
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Enfants — {parent?.firstName} {parent?.lastName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <div className="alert alert-info py-2">Chargement…</div>}

        <div className="d-flex justify-content-between align-items-center mb-2">
          <InputGroup style={{ maxWidth: 320 }}>
            <Form.Control
              placeholder="Rechercher (Prénom, Nom, Lien, Email, Téléphone)…"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
            <Button variant="outline-secondary" onClick={() => setQ('')}>×</Button>
          </InputGroup>
        </div>

        <Table hover responsive size="sm">
          <thead>
            <tr>
              <th>Prénom</th>
              <th>Nom</th>
              <th>Naissance</th>
              <th>Lien</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Actif</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(it => (
              <tr key={it.id}>
                <td>{it.firstName}</td>
                <td>{it.lastName}</td>
                <td>{it.birthDate || '—'}</td>
                <td>{it.relation || '—'}</td>
                <td>{it.email || '—'}</td>
                <td>{it.phone || '—'}</td>
                <td>{it.active ? 'Oui' : 'Non'}</td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted">Aucun enfant.</td></tr>
            )}
          </tbody>
        </Table>

        {total > 0 && renderPagination()}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Fermer</Button>
      </Modal.Footer>
    </Modal>
  );
}
